import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { processAction } from 'src/services/WorkflowService'
import { DatePicker } from 'src/common/DatePickerField/FormikDatePicker'

interface IUpdateInvoiceSentDateModalProps {
  closeModal: () => void
  isOpen: boolean
  inboxItemId: number
  inboxId: string
  action: InboxActionSummary
  data: ObjectiveProcessAction
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  associatedItemId?: string
}

function UpdateInvoiceSentDateModal(props: IUpdateInvoiceSentDateModalProps) {
  const [loading, setLoading] = useState(false)

  useEffect(() => {}, [props.isOpen])

  const handlesOnClose = () => {
    props.closeModal()
  }

  const validationSchema = Yup.object({
    invoiceSentDate: Yup.date().required('Invoice sent date is required'),
    comments: Yup.string().when('requireComment', {
      is: false,
      then: (schema: any) => schema.notRequired(),
      otherwise: (schema: any) => schema.required('Comments are required'),
    }),
  })

  const formInitialValues = {
    invoiceSentDate: undefined,
    comments: '',
    requireComment: props.action.requireComment,
  }

  const isInitialValid = validationSchema.isValidSync(formInitialValues)

  async function processInvoiceSentDateRequest(values: any) {
    setLoading(true)

    let data = new ObjectiveProcessAction()

    data.invoiceSentDate = new Date(
      moment(values.invoiceSentDate, 'YYYY-MM-DD').toISOString()
    )

    if (values.comments) {
      data.comment = values.comments
    }

    processAction(data, props.action.id.toString(), props.inboxItemId.toString())
      .then(() => {
        if (props.handleProcessActionFinish) {
          props.handleProcessActionFinish(props.action)
        }
        setLoading(false)
        props.closeModal()
      })
      .catch((error) => {
        console.error('Error processing invoice sent date:', error)
        setLoading(false)
        props.closeModal()
      })
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={props.isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handlesOnClose()
        }
      }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={props.action.actionName} isModal={true} />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px' }}>
        {loading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          initialValues={formInitialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          isInitialValid={isInitialValid}
          onSubmit={processInvoiceSentDateRequest}
        >
          {({ values, handleChange, errors, touched, isValid, handleBlur }) => (
            <Form>
              <Grid container direction="column" spacing={2}>
                <Grid item direction="row" container xs={6}>
                  <Field
                    name="invoiceSentDate"
                    component={DatePicker}
                    label="Date Invoice Sent"
                    required
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        InputLabelProps: { shrink: true },
                      },
                      openPickerIcon: {
                        fontSize: 'small',
                      },
                    }}
                  />
                </Grid>

                {props.action.displayComment && (
                  <Grid item xs={12}>
                    <TextField
                      onBlur={handleBlur}
                      inputProps={{ id: 'comments' }}
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      name="comments"
                      variant="outlined"
                      value={values.comments}
                      onChange={handleChange}
                      label="Comments"
                      fullWidth
                      multiline
                      minRows={4}
                      required={props.action.requireComment}
                      error={!!(touched.comments && errors.comments)}
                      helperText={(touched.comments && errors.comments) as string}
                    />
                  </Grid>
                )}
              </Grid>

              <DialogActions>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={loading || !isValid}
                >
                  Execute
                </Button>
                <Button onClick={handlesOnClose} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default UpdateInvoiceSentDateModal
